import { useState } from 'react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { downloadPaymentsCsv, getFormattedPaymentsForExport } from '../services/paymentExport.service';
import type { Payment } from '../types/payment.types';

export function usePaymentExport() {
  const [isExporting, setIsExporting] = useState(false);

  // Export all filtered payments
  const handleExport = (
    payments: Payment[],
    dateRange: string,
    userCountry: string | null | undefined
  ) => {
    try {
      setIsExporting(true);
      const exportData = getFormattedPaymentsForExport(payments, dateRange, userCountry ?? 'US');
      if (!exportData.length) {
        toast({
          title: 'No data to export',
          description: 'No payments found for the current filters.',
          variant: 'destructive',
        });
        return;
      }
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      downloadPaymentsCsv(payments, dateRange, userCountry ?? 'US', `payments_export_${todayStr}.csv`);
      toast({
        title: 'Export complete.',
        description: `File downloaded: payments_export_${todayStr}.csv`,
        variant: 'default',
      });
    } catch (err) {
      console.error('Export failed', err);
      toast({
        title: 'Something went wrong.',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Export selected payments
  const handleBulkExportSelected = (
    payments: Payment[],
    selectedPayments: string[],
    dateRange: string,
    userCountry: string | null | undefined,
    onSuccess?: () => void
  ) => {
    if (selectedPayments.length === 0) {
      toast({
        title: 'No payments selected',
        description: 'Please select payments to export.',
        variant: 'destructive',
      });
      return;
    }

    const selectedPaymentsData = payments.filter((p) => selectedPayments.includes(p.id));
    const exportData = getFormattedPaymentsForExport(selectedPaymentsData, dateRange, userCountry ?? 'US');

    if (exportData.length === 0) {
      toast({
        title: 'No data to export',
        description: 'Selected payments have no exportable data.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const fileName = `selected_payments_export_${todayStr}.csv`;
      downloadPaymentsCsv(selectedPaymentsData, dateRange, userCountry ?? 'US', fileName);
      toast({
        title: 'Export complete',
        description: `File downloaded: ${fileName}`,
        variant: 'default',
      });
      onSuccess?.();
    } catch (err) {
      console.error('Export failed', err);
      toast({
        title: 'Export failed',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return {
    isExporting,
    handleExport,
    handleBulkExportSelected,
  };
}
